import React, { useRef } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAutoResizeTextArea } from '../../../hooks/useAutoResizeTextArea';
import AnalysisFileModal from './AnalysisFileModal';

//inyerface

import { iMap } from '../../../../components/type';
import type { SelectedCollectCollection } from '../../../../utils/analysisContextLinks';

interface PromptInputProps {
    prompt: string;
    setPrompt: (prompt: string) => void;
    isLoading: boolean;
    onSubmit: (prompt: string) => void;
    swimlanes: any[];
    fileUploadProps: {
        selectedFile: File | null;
        selectedMaps?: iMap[];
        selectedMapIds: string[];
        selectedCollections?: SelectedCollectCollection[];
        selectedCollectionIds?: string[];
        fileError: string | null;
        handleFileSelect: (file: File | null) => void;
        handleMapSelect: (maps: iMap[] | null) => void;
        handleCollectionSelect?: (collections: SelectedCollectCollection[] | null) => void;
        removeFile: () => void;
        removeMap: (mapId: string) => void;
        clearSelection: () => void;
    };
    isDisableInput?: boolean;
    /** Barre sombre type interview (page analyse entreprise) */
    variant?: 'default' | 'modern';
}

export const PromptInput = ({
    prompt,
    setPrompt,
    isLoading,
    onSubmit,
    swimlanes,
    fileUploadProps,
    isDisableInput,
    variant = 'default'
}: PromptInputProps) => {
    const { t } = useTranslation();
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    useAutoResizeTextArea(textAreaRef.current, prompt);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v'].includes(e.key)) {
            e.stopPropagation();
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit(prompt);
        }
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleFileModalConfirm = (options: { file?: File; maps?: iMap[] }) => {
        if (options.file) {
            fileUploadProps.handleFileSelect(options.file);
        }

        if (options.maps) {
            fileUploadProps.handleMapSelect(options.maps);
        }
    };

    if (variant === 'modern') {
        return (
            <>
                <div className="input-bar ca-input-bar">
                    <div className="input-inner ca-input-inner">
                        <div className="ca-input-left">
                            {isLoading ? (
                                <Loader2 size={20} className="animate-spin ca-input-loader" />
                            ) : (
                                <button
                                    type="button"
                                    className="ca-doc-add-btn"
                                    title={t('features.swimlane.addDocument')}
                                    onClick={handleOpenModal}
                                    disabled={!!isDisableInput}
                                >
                                    <Plus size={20} strokeWidth={2} />
                                </button>
                            )}
                        </div>
                        <textarea
                            ref={textAreaRef}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            placeholder={t('features.swimlane.enterYourPrompt') + '...'}
                            className="input-field ca-input-field"
                            disabled={isLoading || isDisableInput}
                            rows={1}
                        />
                        <div className="input-actions">
                            <button
                                type="button"
                                className="input-btn send-btn"
                                title={t('words.send')}
                                onClick={() => onSubmit(prompt)}
                                disabled={isLoading || isDisableInput}
                            >
                                <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path
                                        d="M13 8L3 3L5.5 8L3 13L13 8Z"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="ca-input-disclaimer">{t('features.swimlane.famlaMake')}</div>
                </div>

                <AnalysisFileModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onConfirm={handleFileModalConfirm}
                    selectedFile={fileUploadProps.selectedFile}
                    selectedMapIds={fileUploadProps.selectedMapIds}
                    selectedMaps={fileUploadProps.selectedMaps}
                    selectedCollectionIds={fileUploadProps.selectedCollectionIds}
                    selectedCollections={fileUploadProps.selectedCollections}
                    swimlanes={swimlanes}
                    fileError={fileUploadProps.fileError}
                    handleFileSelect={fileUploadProps.handleFileSelect}
                    handleMapSelect={fileUploadProps.handleMapSelect}
                    handleCollectionSelect={fileUploadProps.handleCollectionSelect}
                    clearSelection={fileUploadProps.clearSelection}
                />
            </>
        );
    }

    return (
        <>
            <div className="">
                <div className="relative px-2">
                    <div className={`relative bg-gray-100 flex ${prompt ? "rounded-2xl" : "rounded-full"}`}>
                        <div className='absolute left-2 top-1/2 -translate-y-1/2'>
                            {isLoading ? (
                                <Loader2 size={20} className="animate-spin" />
                            ) : (
                                <div className='flex ml-2 cursor-pointer' title={t('features.swimlane.addDocument')} onClick={handleOpenModal}>
                                    <Plus size={20} />
                                </div>
                            )}
                        </div>

                        <textarea
                            ref={textAreaRef}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            placeholder={t('features.swimlane.enterYourPrompt') + '...'}
                            className={`w-full pr-4 pl-12 py-3 border-none bg-transparent text-gray-700 placeholder-gray-500 ${prompt ? "rounded-2xl" : "rounded-full"} focus:ring-1 focus:ring-gray-300 focus:outline-none resize-none`}
                            disabled={isLoading || isDisableInput}
                            style={{
                                height: "6vh",
                                minHeight: '48px',
                                maxHeight: '700px',
                                overflow: 'hidden'
                            }}
                        />
                    </div>
                </div>
                <p className="mt-2 pb-1 text-xs text-gray-500 text-center">
                    {t('features.swimlane.famlaMake')}
                </p>
            </div>

            <AnalysisFileModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleFileModalConfirm}
                selectedFile={fileUploadProps.selectedFile}
                selectedMapIds={fileUploadProps.selectedMapIds}
                selectedMaps={fileUploadProps.selectedMaps}
                selectedCollectionIds={fileUploadProps.selectedCollectionIds}
                selectedCollections={fileUploadProps.selectedCollections}
                swimlanes={swimlanes}
                fileError={fileUploadProps.fileError}
                handleFileSelect={fileUploadProps.handleFileSelect}
                handleMapSelect={fileUploadProps.handleMapSelect}
                handleCollectionSelect={fileUploadProps.handleCollectionSelect}
                clearSelection={fileUploadProps.clearSelection}
            />
        </>
    );
};
